import { fieldPositions, flightTimeScale } from "@Context/game/builder/builder-context";
import ScenarioActionType from "@Enums/ScenarioActionType";
import { Point } from "@Shared/types/ScenarioTypes";
import { clamp, isArray, isEqual } from "lodash-es";
import { BuilderState, FieldPosition, Player, PlayerAction, ScenarioClip } from "../models/builder-data-types";
import { createNewPoint, getBeziers, getEndTime, getNewNext, getPassFlightTime, getPointAtTime, isValidIndex } from "./utilities";

const getInitials = (name: string, index: number) => {
  if (name === "") {
    return "P" + (index + 1);
  }

  const fieldPosition = fieldPositions.find((fieldPosition) => fieldPosition.name === name);
  if (fieldPosition) {
    return fieldPosition.initials;
  }

  return "?";
};

const getFieldPositionFromName = (name: string) => {
  //replace all instances of '_' with ' ', convert to lowercase, remove all digits, trim whitespace
  const cleanedName = name.replace(/_/g, " ").toLocaleLowerCase().replace(/\d/g, "").trim();

  return fieldPositions.find((fieldPosition) => {
    if (isEqual(cleanedName, fieldPosition.name.toLowerCase())) {
      return fieldPosition;
    }

    // JS not comparison strings properly without isEqual, so long-winded search here
    if (fieldPosition.synonyms) {
      for (let i = 0; i < fieldPosition.synonyms.length; i++) {
        if (isEqual(cleanedName, fieldPosition.synonyms[i])) {
          return fieldPosition;
        }
      }
    }

    return undefined;
  });
};

const getFieldPositionFromNumber = (number: number) => {
  return fieldPositions.find((fieldPosition) => fieldPosition.jerseyNumber === number);
};

const getExportPlayerName = (player: Player) => {
  // replace all spaces with '_', convert to titlecase, prepend with team and jersey number
  // e.g. '1_7_John_Doe'
  return player.team + "_" + player.jerseyNumber + "_" + player.name.replace(/\b\w/g, (l) => l.toUpperCase()).replace(/ /g, "_");
};

const addPlayer = (state: BuilderState, dispatch: (data: any) => void, position: Point, fieldPosition: FieldPosition, team: number) => {
  const id = state.availableIds[0];
  const newIds = state.availableIds.slice(1);

  const newPlayers = new Map<number, Player>();
  state.clip.scenario.players.forEach((value, key) => {
    newPlayers.set(key, value);
  });

  const newPlayer: Player = {
    name: fieldPosition.name,
    initials: fieldPosition.initials,
    jerseyNumber: fieldPosition.jerseyNumber,
    id: id,
    isPlayer: false, //as in: the user
    team: team,
    points: [{ position: position, prev: position, next: position, t: 0 }],
    actions: [],
    beziers: [],
  };

  newPlayers.set(id, newPlayer);

  if (team == 1) {
    const newTeamMates = [];
    for (let i = 0; i < state.availableTeamMates.length; i++) {
      if (state.availableTeamMates[i].name !== fieldPosition.name) {
        newTeamMates.push(state.availableTeamMates[i]);
      }
    }
    dispatch({ availableTeamMates: newTeamMates });
  } else {
    const newOpponents = [];
    for (let i = 0; i < state.availableOpponents.length; i++) {
      if (state.availableOpponents[i].name !== fieldPosition.name) {
        newOpponents.push(state.availableOpponents[i]);
      }
    }
    dispatch({ availableOpponents: newOpponents });
  }

  dispatch({
    clip: {
      ...state.clip,
      scenario: {
        ...state.clip.scenario,
        players: newPlayers,
      },
    },
    showFieldRadial: false,
    showPlayerRadial: false,
    availableIds: newIds,
  });
};

const updatePlayer = (state: BuilderState, dispatch: (data: any) => void, player: Player) => {
  const newPlayers = new Map<number, Player>();
  state.clip.scenario.players.forEach((value, key) => {
    newPlayers.set(key, value.id !== player.id ? { ...value } : { ...player });
  });

  dispatch({ 
    clip: {
      ...state.clip,
      scenario: {
        ...state.clip.scenario,
        players: newPlayers,
      },
    },
    showPlayerRadial: false 
  });
};

const updateAction = (state: BuilderState, dispatch: (data: any) => void, playerId: number, actionIndex: number, action: PlayerAction) => {
  const player = getPlayer(state.clip, playerId);
  if (!player || !isValidIndex(actionIndex, player.actions)) return;

  const newPlayer = { ...player };
  newPlayer.actions = [];
  for (let i = 0; i < player.actions.length; i++) {
    newPlayer.actions.push({ ...player.actions[i] });
  }
  newPlayer.actions[actionIndex] = action;

  updatePlayer(state, dispatch, newPlayer);
};

const deletePlayer = (state: BuilderState, dispatch: (data: any) => void, player: Player, playerIndex: number) => {
  if (!state.clip.scenario.players.has(state.selectedPlayer)) return;

  if (player.team == 1) {
    const newTeamMates = [...state.availableTeamMates];
    newTeamMates.push({ name: player.name, initials: player.initials, jerseyNumber: player.jerseyNumber });
    dispatch({ availableTeamMates: newTeamMates });
  } else {
    const newOpponents = [...state.availableOpponents];
    newOpponents.push({ name: player.name, initials: player.initials, jerseyNumber: player.jerseyNumber });
    dispatch({ availableOpponents: newOpponents });
  }

  const newIds = [...state.availableIds];
  newIds.push(player.id);
  dispatch({ availableIds: newIds });

  const newPlayers = new Map<number, Player>();
  state.clip.scenario.players.forEach((value, key) => {
    if (key !== player.id) {
      newPlayers.set(key, value);
    }
  });

  dispatch({
    clip: {
      ...state.clip,
      scenario: {
        ...state.clip.scenario,
        players: newPlayers,
      },
    },
    selectedPlayer: -1,
    showPlayerRadial: false,
  });
};

const addAction = (
  state: BuilderState,
  dispatch: (data: any) => void,
  player: Player,
  actionType: ScenarioActionType,
  targetPlayer: number,
  targetPosition: Point | null,
  startTime: number,
  endTime: number,
  isGenerated: boolean = false
) => {
  console.log("Adding action", player, actionType, targetPlayer, targetPosition, startTime, endTime);

  const newPlayer = { ...player, id: player.id };
  newPlayer.actions = [];
  for (let i = 0; i < player.actions.length; i++) {
    newPlayer.actions.push({ ...player.actions[i] });
  }

  const newStartTime = clamp(startTime, 0, state.clip.scenario.endTime);
  let newTime = 0;
  if (actionType === ScenarioActionType.PassTarget) {
    const kickPos = getPointAtTime(player.points, newStartTime, player.beziers);
    const receiver = state.clip.scenario.players.get(targetPlayer);
    if (receiver) {
      const receiverPos = getPointAtTime(receiver.points, newStartTime, receiver.beziers);
      newTime = newStartTime + getPassFlightTime(kickPos.position, receiverPos.position, flightTimeScale);
    }
  }

  let newIndex = 0;
  if (actionType === ScenarioActionType.Score) {
    newTime = 1;
    newIndex = 50;
  }

  newPlayer.actions.push({
    type: actionType,
    startTime: clamp(startTime, 0, state.clip.scenario.endTime),
    endTime: clamp(endTime, 0, state.clip.scenario.endTime),
    targetPlayer: targetPlayer,
    targetPosition: targetPosition || { x: 0, y: 0 },
    data: {},
    index: newIndex,
    height: 0,
    direction: 0,
    time: newTime,
    isGenerated: isGenerated,
  });

  updatePlayer(state, dispatch, newPlayer);

  dispatch({
    showPlayerRadial: false,
  });
};

const addWaypoint = (state: BuilderState, dispatch: (data: any) => void, playerId: number, position: Point) => {
  const newPlayers = new Map<number, Player>();
  state.clip.scenario.players.forEach((value, key) => {
    newPlayers.set(key, { ...value });
  });

  const oldPlayer = state.clip.scenario.players.get(playerId);
  const player = newPlayers.get(playerId);
  if (!player) return;
  const newPlayer = { ...player };
  newPlayer.points = [];
  if (oldPlayer) {
    oldPlayer.points.forEach((point) => {
      if (isArray(newPlayer.points)) newPlayer.points.push({ ...point });
    });
  }

  newPlayer.points[newPlayer.points.length - 1].next = getNewNext(newPlayer.points[newPlayer.points.length - 1], position);

  const endTime = getEndTime(newPlayer);
  const newTime = state.time > endTime ? state.time : endTime + 1;
  if (newPlayer.points.length > 0) {
    newPlayer.points.push(createNewPoint(newPlayer.points[newPlayer.points.length - 1], position, newTime));
  } else {
    newPlayer.points.push({
      position: { x: position.x, y: position.y },
      prev: { x: position.x, y: position.y },
      next: { x: position.x, y: position.y },
      t: newTime,
    });
  }

  newPlayer.beziers = getBeziers(newPlayer.points);
  newPlayers.set(playerId, newPlayer);

  dispatch({
    clip: {
      ...state.clip,
      scenario: {
        ...state.clip.scenario,
        players: newPlayers,
      },
    },
    endTime: Math.max(newTime, state.clip.scenario.endTime),
  });
};

const insertWaypoint = (state: BuilderState, dispatch: (data: any) => void, player: Player, afterIndex: number, position: Point, time: number) => {
  if (!player || !isValidIndex(afterIndex, player.points)) return;

  const newPlayer = { ...player };
  newPlayer.points = [];
  const endTime = getEndTime(player);
  const prevWaypointTime = player.points[afterIndex].t || 0;
  const nextWaypointTime = player.points[afterIndex + 1].t || endTime;
  let newTime = time;
  if (time <= prevWaypointTime || time >= nextWaypointTime) {
    newTime = prevWaypointTime + (nextWaypointTime - prevWaypointTime) / 2;
  }
  const newWaypoint = createNewPoint(player.points[afterIndex], position, newTime);

  for (let i = 0; i <= afterIndex; i++) {
    newPlayer.points.push({ ...player.points[i] });
  }
  newPlayer.points.push(newWaypoint);
  for (let i = afterIndex + 1; i < player.points.length; i++) {
    newPlayer.points.push({ ...player.points[i] });
  }

  newPlayer.beziers = getBeziers(newPlayer.points);
  const newPlayers = new Map<number, Player>();
  state.clip.scenario.players.forEach((value, key) => {
    newPlayers.set(key, { ...value });
  });
  newPlayers.set(player.id, newPlayer);

  dispatch({
    clip: {
      ...state.clip,
      scenario: {
        ...state.clip.scenario,
        players: newPlayers,
      },
    },
    selectedWaypoint: afterIndex + 1,
  });
};

const deleteWaypoint = (state: BuilderState, dispatch: (data: any) => void, player: Player, waypointIndex: number) => {
  console.log("Deleting waypoint", player, waypointIndex);
  if (!player || !isValidIndex(waypointIndex, player.points)) return;

  const newPlayer = { ...player };
  newPlayer.points = [];
  for (let i = 0; i < player.points.length; i++) {
    newPlayer.points.push({ ...player.points[i] });
  }
  newPlayer.points.splice(state.selectedWaypoint, 1);

  const newPlayers = new Map<number, Player>();
  state.clip.scenario.players.forEach((value, key) => {
    newPlayers.set(key, value.id !== player.id ? { ...value } : newPlayer);
  });

  dispatch({
    clip: {
      ...state.clip,
      scenario: {
        ...state.clip.scenario,
        players: newPlayers,
      },
    },
    selectedWaypoint: -1,
    showWaypointRadial: false,
  });
};

const getPlayer = (clip: ScenarioClip, id: number) => {
  return clip.scenario.players.get(id) || undefined;
};

const getSelectedPlayer = (state: BuilderState) => {
  return state.clip.scenario.players.get(state.selectedPlayer) || undefined;
};

export {
  addAction,
  addPlayer,
  addWaypoint,
  deletePlayer,
  deleteWaypoint,
  getExportPlayerName,
  getFieldPositionFromName,
  getFieldPositionFromNumber,
  getInitials,
  getPlayer,
  getSelectedPlayer,
  insertWaypoint,
  updateAction,
  updatePlayer,
};
